import type { AudioPlayer } from "../audio/AudioPlayer";
import type { addAudioBreadcrumb } from "../audio/audioSentry";
import type { PlayerProgress } from "./progressState";
import {
  selectActiveQueueItemId,
  selectActiveSongId,
  selectSeekable,
} from "./selectors";
import type { PlayerStore } from "./types";

type BreadcrumbData = Record<string, unknown>;

export function createPlayerBreadcrumbs(
  audioPlayer: AudioPlayer,
  progressState: PlayerProgress["progressState"],
  breadcrumb: typeof addAudioBreadcrumb,
) {
  function getPlayerBreadcrumbData(state: PlayerStore): BreadcrumbData {
    const audioState = audioPlayer.getState();

    return {
      activeSongId: selectActiveSongId(state),
      activeQueueItemId: selectActiveQueueItemId(state),
      activeQueueType: state.activeQueueType,
      nativeTrackId: audioPlayer.activeTrackId,
      status: state.status,
      userIntent: state.userIntent,
      seekable: selectSeekable(state),
      positionSeconds: progressState.position.value,
      durationSeconds: progressState.duration.value,
      bufferedSeconds: progressState.buffered.value,
      progressSongId: progressState.target?.songId ?? null,
      progressQueueItemId: progressState.target?.queueItemId ?? null,
      progressPositionConfirmed: progressState.isPositionConfirmed,
      pendingSeekSongId: state.pendingSeek?.songId ?? null,
      pendingSeekQueueItemId: state.pendingSeek?.queueItemId ?? null,
      pendingSeekPositionMs: state.pendingSeek?.positionMs ?? null,
      nativePlaybackState: audioState.playbackState,
      nativePositionMs: audioState.positionMs,
      nativeDurationMs: audioState.durationMs,
      nativeBufferedPositionMs: audioState.bufferedPositionMs,
    };
  }

  function addPlayerBreadcrumb(
    operation: string,
    state: PlayerStore,
    data: BreadcrumbData = {},
    level: "info" | "warning" | "error" = "info",
  ) {
    breadcrumb(
      operation,
      {
        ...getPlayerBreadcrumbData(state),
        ...data,
      },
      level,
    );
  }

  return { getPlayerBreadcrumbData, addPlayerBreadcrumb };
}
